import { ADModalBody, ADModalFooter } from "src/componentsUtils/AdvancedDragModal";
import { GlassInputControl, GlassInputGroup } from "./GlassInput";
import { useContext, useEffect, useMemo, useState } from "react";
import { MapitStateContext } from "src/states/MapitState";
import { GlassButton, GlassButtonRow } from "./GlassButtons";
import { Typeahead } from "react-bootstrap-typeahead";
import { Localization } from "@viamap/viamap2-common";
import jsPDF, { TextOptionsLight } from "jspdf";
import { VLine } from "./MitGlassComponents";
import { Spinner } from "react-bootstrap";
import { message } from "./Message";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { addDatarapportImage } from "./DatarapportShared";
import { getGaeldendeJordstykker, hentDataOgBelligenhed, SFEAndColorList } from "./MatrikelRapportData";

export function MatrikelRapport(props: {
   currentRapport: string,
   availableRapports: { value: string, label: string }[],
   onSelectRapport: (a: string) => void,
   onGenerate: (data: any, pdf: jsPDF) => void
}) {
   const { state: mapitState } = useContext(MapitStateContext);
   const [loading, setLoading] = useState(false);


   let usableLayers = useMemo(() => {
      return Object.values(mapitState.layers).filter(layer => layer.propertiesInGeoJson?.includes("SFEnummer"));
   }, [mapitState.layers])

   const [layerId, setLayerId] = useState<number[]>(usableLayers.map((a) => a.layerId));

   useEffect(() => {
      setLayerId((a) => a.filter((b) => usableLayers.map((a) => a.layerId).includes(b)));
   }, [mapitState.layers])

   let colorList = useMemo(() => {
      return SFEAndColorList(
         layerId.map((a) => mapitState.layers[a])
      );
   }, [layerId])

   return (
      <>
         <ADModalBody>
            <GlassInputGroup>
               <label>{Localization.getText("Document")}</label>
               <GlassInputControl>
                  <select disabled={false}
                     value={props.currentRapport}
                     onChange={(e) => {
                        let value = e.currentTarget.value;
                        props.onSelectRapport(value);
                     }}>
                     {
                        props.availableRapports.map((a) => {
                           return (
                              <option key={a.value} value={a.value}>{a.label}</option>
                           )
                        })
                     }
                  </select>
               </GlassInputControl>
               <label>{Localization.getText("Property layer")}</label>
               <Typeahead
                  multiple={true}
                  id="layerTypeahead"
                  options={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname }))}
                  labelKey={"datasetname"}
                  placeholder={Localization.getText("please select") + " " + Localization.getText("Property layer")}
                  emptyLabel={Localization.getText("No results found")}
                  onChange={(selected) => setLayerId(selected.map((a: any) => a.id))}
                  selected={usableLayers.map((a) => ({ id: a.layerId, datasetname: a.datasetname })).filter((a) => layerId.includes(a.id))}
               />
               <label></label>
               <GlassButtonRow>
                  <GlassButton onClick={() => setLayerId(usableLayers.map((a) => a.layerId))}>{Localization.getText("Select All")}</GlassButton>
                  <GlassButton onClick={() => setLayerId([])}>{Localization.getText("Remove All")}</GlassButton>
               </GlassButtonRow>
               <VLine color={"#9d9d9d"} /><VLine color={"#9d9d9d"} />
               <label>{Localization.getText("Properties")} {colorList.length}</label>
               <div style={{ display: "flex", flexWrap: "wrap", gap: "2px" }}>
                  {colorList.map((a) => {
                     return (
                        <div
                           key={a.sfe}
                           title={a.sfe + ""}
                           style={{ background: a.color, borderRadius: "5px", padding: "3px", display: "flex", justifyContent: "center" }}>
                           <MIBIcon icon="property" style={{ aspectRatio: "1", color: "white" }} />
                        </div>
                     )
                  })}
               </div>
            </GlassInputGroup>


         </ADModalBody>
         <ADModalFooter>
            {loading ? <Spinner /> : <GlassButton disabled={!colorList.length} onClick={async () => {
               setLoading(true);
               try {
                  let data = await hentDataOgBelligenhed(colorList)
                  let pdf = await createPDF(data, colorList);
                  if (pdf) {
                     props.onGenerate(data, pdf);
                  }
               } catch (e: unknown) {
                  if (e instanceof Error) {
                     message.error(Localization.getFormattedText("An error occurred while generating the report. Please try again later or contact support. Error details: {err}", { err: e.message }));
                  } else {
                     message.error(Localization.getText("An error occurred while generating the report. Please try again later or contact support."));
                  }
                  setLoading(() => false);
               }

            }}
            >{Localization.getText("Generate")}</GlassButton>}
         </ADModalFooter>
      </>
   );
}

async function createPDF(data: any[], colorList: { sfe: number, color: string }[]): Promise<jsPDF | null> {

   let doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4" });

   const arealFormat = (a: number) => Intl.NumberFormat("da-DK", { style: "decimal", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(a);
   const vejFormat = (a: number) => Intl.NumberFormat("da-DK", { style: "decimal", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(a);

   if (data.length == 0) {
      return null;
   }

   doc.text("Matrikeloversigt", 110, 10, { align: "center" });
   doc.setFontSize(7);
   doc.text("Ejendomstabel", 15, 10);
   doc.text("Mapit", 15, 13);
   doc.text("Dato: " + new Date().toLocaleDateString("da-dk"), 220 - 25, 10, { align: "right" });
   doc.text("Side: 1", 220 - 25, 13, { align: "right" });
   let currentPage = 1;


   let top = 15;
   let left = 15;
   let height = 5;

   let headerHeight = 14;
   let totalHeight = 5;
   doc.setFontSize(12);
   data.forEach((a, idx) => {
      let jordstykker = getGaeldendeJordstykker(a);
      let length = jordstykker.length * height + headerHeight + totalHeight;
      if (top + length > 260) {
         doc.cellAddPage();
         currentPage++;
         doc.setFontSize(7);
         doc.setFont(undefined as any, "normal");
         doc.text("Ejendomstabel", 15, 10);
         doc.text("Mapit", 15, 13);
         doc.text("Dato: " + new Date().toLocaleDateString("da-dk"), 220 - 25, 10, { align: "right" });
         doc.text("Side: " + currentPage, 220 - 25, 13, { align: "right" });
         top = 15;
      } else if (idx > 0) {
         top += 2;
         doc.setFillColor("#000000");
         // customCell(doc, left, 5, 10, 18, "", -1, "left");
         doc.setFillColor("#000000");
      }
      let start = top;

      // doc.cellAddPage();
      doc.setFontSize(12);
      customCell(doc, left, top, 10, 14, " ", 0, "left");
      customCell(doc, left + 10, top, 170, 14, a.adresse, 0, "left");
      doc.setFontSize(9);
      doc.text("SFE: " + a.BFEnummer, 27, top + 7.5);
      doc.setFontSize(12);
      top += 9;
      customCell(doc, left, top, 10, height, " ", 1, "left")
      customCell(doc, left + 10, top, 30, height, "Matr.nr", 1, "left", true)
      customCell(doc, left + 40, top, 80, height, "Ejerlav", 1, "left", true)
      customCell(doc, left + 120, top, 30, height, "Areal", 1, "left", true)
      customCell(doc, left + 150, top, 30, height, "Vejareal", 1, "left", true)
      jordstykker.forEach((a, idx) => {
         top += 5;
         customCell(doc, left, top, 10, height, " ", 2 + idx, "left")
         customCell(doc, left + 10, top, 30, height, a.matrikelnummer || " ", 2 + idx, "left")
         customCell(doc, left + 40, top, 80, height, a.jordstykkeLiggerIEjerlav?.ejerlavsnavn || " ", 2 + idx, "left")
         customCell(doc, left + 120, top, 30, height, arealFormat(a.registreretAreal / 10000) + " ha" || " ", 2 + idx, "right")
         customCell(doc, left + 150, top, 30, height, vejFormat(a.vejareal) + "m²" || " ", 2 + idx, "right")
      });
      top += 5;
      // customCell(doc, left, top, 10, height, " ", 2+jordstykker.length, "left")
      // customCell(doc, left+10, top, 30, height, "", 2+jordstykker.length, "left")
      customCell(doc, left, top, 120, height, "Total", 2 + jordstykker.length, "right", true)
      customCell(doc, left + 120, top, 30, height, arealFormat(jordstykker.reduce((sum, a) => sum + a.registreretAreal, 0) / 10000) + " ha", 2 + jordstykker.length, "right", true)
      customCell(doc, left + 150, top, 30, height, vejFormat(jordstykker.reduce((sum, a) => sum + a.vejareal, 0)) + "m²", 2 + jordstykker.length, "right", true)


      const colorItem = colorList.find((b) => b.sfe == a.BFEnummer);
      if (colorItem) {
         doc.setFillColor(colorItem.color);
      }
      doc.rect(15, start, 10, length, "FD");

      top = start + length;
   });

   // TOTALER
   if (top + 10 > 260) {
      doc.cellAddPage();
      currentPage++;
      doc.setFontSize(7);
      doc.setFont(undefined as any, "normal");
      doc.text("Ejendomstabel", 15, 10);
      doc.text("Mapit", 15, 13);
      doc.text("Dato: " + new Date().toLocaleDateString("da-dk"), 220 - 25, 10, { align: "right" });
      doc.text("Side: " + currentPage, 220 - 25, 13, { align: "right" });
      top = 15;
   }
   top += 2;
   doc.line(left, top, left + 180, top);
   top += 2;
   customCell(doc, left, top, 10, height, "", 0, "left")

   // Draw crossed lines for total
   for (let i = 0; i <= 5; i++) {
      doc.line(left, top + i, left + 10, top + i);
   }

   customCell(doc, left + 10, top, 110, height, "Samlet areal", 0, "right", true)
   customCell(doc, left + 120, top, 30, height, arealFormat(data.reduce((sum, a) => sum + getGaeldendeJordstykker(a).reduce((sum2, b) => sum2 + b.registreretAreal, 0), 0) / 10000) + " ha", 0, "right", true)
   customCell(doc, left + 150, top, 30, height, vejFormat(data.reduce((sum, a) => sum + getGaeldendeJordstykker(a).reduce((sum2, b) => sum2 + b.vejareal, 0), 0)) + "m²", 0, "right", true)

   await addDatarapportImage(doc, top);

   return doc;
}

function customCell(doc: jsPDF, x, y, width, height, value, ln, align?: TextOptionsLight["align"], bold?: boolean) {
   doc.setDrawColor("#000000");
   doc.rect(x, y, width, height, "S");
   if (bold) {
      doc.setFont(undefined as any, "bold");
   } else {
      doc.setFont(undefined as any, "normal");
   }

   if (align == "right") {
      doc.text(value + "" || " ", x + width - 2, y + 4, { align: align || "left" });
   } else {
      doc.text(value + "" || " ", x + 2, y + 4, { align: align || "left", });
   }
}

